import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

function parseIncomeRange(range) {
  if (!range) return 5000;
  const clean = String(range)?.replace(/[$,\s]/g, '')?.toLowerCase();
  const parts = clean?.split('-');
  const parse = (s) => {
    if (!s) return 0;
    const n = parseFloat(s?.replace('k', ''));
    return s?.includes('k') ? n * 1000 : n;
  };
  if (parts?.length === 2) return (parse(parts?.[0]) + parse(parts?.[1])) / 2;
  return parse(parts?.[0]) || 5000;
}

export default function AiBudgetOptimizer({
  categories,
  selectedMonth,
  totalBudget,
  onApplySuggestion,
  onApplyAll,
  onCategoryInsights,
}) {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState(null);
  const [goalInsights, setGoalInsights] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [aiError, setAiError] = useState(false);
  const [tooltipIdx, setTooltipIdx] = useState(null);
  const [applyingIdx, setApplyingIdx] = useState(null);
  const [applyingAll, setApplyingAll] = useState(false);

  // Reset when month changes
  useEffect(() => {
    setSuggestions(null);
    setGoalInsights('');
    setAiError(false);
    onCategoryInsights?.({});
  }, [selectedMonth]);

  const fetchContextData = useCallback(async () => {
    if (!user) return {};
    const threeMonthsAgo = new Date();
    threeMonthsAgo?.setMonth(threeMonthsAgo?.getMonth() - 3);
    const dateStr = threeMonthsAgo?.toISOString()?.split('T')?.[0];

    const [txResult, goalsResult, profileResult] = await Promise.all([
      supabase
        ?.from('transactions')
        ?.select('amount, transaction_date, spending_categories(name)')
        ?.eq('user_id', user?.id)
        ?.eq('transaction_type', 'expense')
        ?.gte('transaction_date', dateStr),
      supabase
        ?.from('financial_goals')
        ?.select('title, target_amount, current_amount, monthly_contribution, deadline')
        ?.eq('user_id', user?.id)
        ?.eq('goal_status', 'active'),
      supabase
        ?.from('user_profiles')
        ?.select('income_range, risk_tolerance_score')
        ?.eq('id', user?.id)
        ?.maybeSingle(),
    ]);

    const spendMap = {};
    (txResult?.data || [])?.forEach((tx) => {
      const catName = tx?.spending_categories?.name || 'Other';
      if (!spendMap?.[catName]) spendMap[catName] = { total: 0 };
      spendMap[catName].total += Number(tx?.amount || 0);
    });
    const avgSpend = Object.entries(spendMap)?.map(([cat, v]) => ({
      category: cat,
      avg_monthly_spend: Math.round(v?.total / 3),
    }));

    const goals = (goalsResult?.data || [])?.map((g) => ({
      name: g?.title,
      target_amount: Number(g?.target_amount || 0),
      current_amount: Number(g?.current_amount || 0),
      monthly_contribution: Number(g?.monthly_contribution || 0),
      deadline: g?.deadline,
    }));

    const profile = profileResult?.data || {};
    const monthlyIncome = parseIncomeRange(profile?.income_range) || totalBudget;
    return { avgSpend, goals, profile, monthlyIncome };
  }, [user, totalBudget]);

  const generateBudget = useCallback(async () => {
    if (!categories?.length) return;
    setIsLoading(true);
    setAiError(false);
    setSuggestions(null);
    setGoalInsights('');
    onCategoryInsights?.({});

    try {
      const { avgSpend, goals, profile, monthlyIncome } = await fetchContextData();

      const currentAllocations = categories?.map((c) => ({
        category: c?.category,
        current_budget: c?.allocated_amount,
        spent_amount: c?.spent_amount,
      }));

      const systemPrompt = `You are FinVisAR, an expert AI financial advisor. Analyze spending patterns and financial goals to suggest optimal monthly budget allocations. Respond with valid JSON only — no markdown, no text outside JSON.`;

      const userPrompt = `Return a JSON object with exactly these keys:
1. "allocations": array of { category: string, current_budget: number, suggested_budget: number, change_amount: number, change_percent: number, reasoning: string (max 20 words) }
2. "goal_insights": string (2-3 sentences on how allocations help reach goals)
3. "category_insights": object mapping each category name to a behavioral nudge string (max 12 words, e.g. "You typically overspend here by 18%" or "Consistently under budget — consider reallocating")

Financial data:
- Monthly income: $${monthlyIncome}
- Total budget cap: $${totalBudget}
- Risk tolerance: ${profile?.risk_tolerance_score || 5}/10
- Current allocations: ${JSON.stringify(currentAllocations)}
- 3-month avg spend: ${JSON.stringify(avgSpend)}
- Active goals: ${JSON.stringify(goals)}

Rules: suggested budgets sum <= $${totalBudget}. change_amount = suggested - current. change_percent = round((change_amount/current_budget)*100,1). Only include categories from current allocations.`;

      const result = await getChatCompletion(
        'OPEN_AI',
        'gpt-5',
        [{ role: 'system', content: systemPrompt }, { role: 'user', content: userPrompt }],
        { max_completion_tokens: 1500, response_format: { type: 'json_object' } }
      );

      const content = result?.choices?.[0]?.message?.content || '';
      const parsed = JSON.parse(content);

      setSuggestions(parsed?.allocations || []);
      setGoalInsights(parsed?.goal_insights || '');
      onCategoryInsights?.(parsed?.category_insights || {});
    } catch (e) {
      console.error('AI Budget Optimizer error:', e);
      setAiError(true);
    } finally {
      setIsLoading(false);
    }
  }, [categories, fetchContextData, totalBudget, onCategoryInsights]);

  const handleApply = async (suggestion, idx) => {
    setApplyingIdx(idx);
    await onApplySuggestion?.(suggestion?.category, suggestion?.suggested_budget);
    setApplyingIdx(null);
  };

  const handleApplyAll = async () => {
    setApplyingAll(true);
    await onApplyAll?.(suggestions);
    setApplyingAll(false);
  };

  return (
    <div className="mb-8">
      <div className="rounded-2xl overflow-hidden border border-[rgba(0,229,255,0.2)] shadow-lg shadow-[rgba(0,229,255,0.05)]">
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-[#0D2D3A] via-[#0A3040] to-[#0D2D3A] border-b border-[rgba(0,229,255,0.15)] px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#06B6D4] flex items-center justify-center shadow-lg shadow-[rgba(0,229,255,0.3)] flex-shrink-0">
              <Icon name="Sparkles" size={18} color="#0D1B2A" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-[#F8FAFC] font-bold text-base">AI Budget Optimizer</h2>
                <span className="px-2 py-0.5 rounded-full bg-[rgba(0,229,255,0.15)] border border-[rgba(0,229,255,0.3)] text-[#00E5FF] text-[10px] font-semibold tracking-wide">
                  Powered by AI
                </span>
              </div>
              <p className="text-[#64748B] text-xs mt-0.5">Smart allocations based on your spending patterns &amp; goals</p>
            </div>
          </div>
          <button
            onClick={generateBudget}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#00E5FF] to-[#06B6D4] text-[#0D1B2A] font-semibold text-sm hover:opacity-90 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-md shadow-[rgba(0,229,255,0.2)] self-start sm:self-auto"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-[#0D1B2A] border-t-transparent rounded-full animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Icon name="Wand2" size={15} color="#0D1B2A" />
                Generate Smart Budget
              </>
            )}
          </button>
        </div>

        {/* Body */}
        <div className="bg-[rgba(13,27,42,0.95)] p-5">
          {/* Error State */}
          {aiError && !isLoading && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)]">
              <Icon name="AlertCircle" size={18} color="#EF4444" />
              <div className="flex-1">
                <p className="text-[#EF4444] text-sm font-medium">AI Unavailable</p>
                <p className="text-[#64748B] text-xs mt-0.5">Unable to generate suggestions right now. Please try again later.</p>
              </div>
              <button onClick={generateBudget} className="text-xs text-[#00E5FF] hover:underline flex-shrink-0">Retry</button>
            </div>
          )}

          {/* Loading Skeleton */}
          {isLoading && (
            <div className="space-y-3 animate-pulse">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-3 w-32 bg-[rgba(255,255,255,0.08)] rounded-full" />
                <div className="h-3 w-20 bg-[rgba(255,255,255,0.06)] rounded-full" />
              </div>
              {Array.from({ length: 5 })?.map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
                  <div className="w-24 h-4 bg-[rgba(255,255,255,0.08)] rounded-full" />
                  <div className="flex-1" />
                  <div className="w-16 h-4 bg-[rgba(255,255,255,0.06)] rounded-full" />
                  <div className="w-16 h-4 bg-[rgba(0,229,255,0.1)] rounded-full" />
                  <div className="w-16 h-4 bg-[rgba(255,255,255,0.06)] rounded-full" />
                  <div className="w-14 h-7 bg-[rgba(255,255,255,0.06)] rounded-lg" />
                </div>
              ))}
              <div className="mt-4 h-16 bg-[rgba(0,229,255,0.04)] rounded-xl border border-[rgba(0,229,255,0.08)]" />
            </div>
          )}

          {/* Empty / Initial State */}
          {!isLoading && !aiError && !suggestions && (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <div className="w-14 h-14 rounded-2xl bg-[rgba(0,229,255,0.08)] border border-[rgba(0,229,255,0.15)] flex items-center justify-center">
                <Icon name="BrainCircuit" size={26} color="#00E5FF" />
              </div>
              <p className="text-[#E2E8F0] font-medium text-sm">Ready to optimize your budget</p>
              <p className="text-[#64748B] text-xs text-center max-w-xs">
                Click &quot;Generate Smart Budget&quot; to get AI-powered allocation suggestions based on your spending history and financial goals.
              </p>
            </div>
          )}

          {/* Suggestions Table */}
          {!isLoading && suggestions && suggestions?.length > 0 && (
            <>
              {/* Table Header — hidden on mobile */}
              <div className="hidden sm:grid grid-cols-[1fr_90px_110px_90px_80px] gap-2 px-3 mb-2">
                <span className="text-[#64748B] text-xs font-medium">Category</span>
                <span className="text-[#64748B] text-xs font-medium text-right">Current</span>
                <span className="text-[#64748B] text-xs font-medium text-right">AI Suggested</span>
                <span className="text-[#64748B] text-xs font-medium text-right">Change</span>
                <span className="text-[#64748B] text-xs font-medium text-right">Action</span>
              </div>

              <div className="space-y-2">
                {suggestions?.map((s, idx) => {
                  const isIncrease = (s?.change_amount || 0) > 0;
                  const isDecrease = (s?.change_amount || 0) < 0;
                  const changeColor = isIncrease ? '#10B981' : isDecrease ? '#EF4444' : '#64748B';
                  const changeIcon = isIncrease ? 'TrendingUp' : isDecrease ? 'TrendingDown' : 'Minus';
                  return (
                    <div
                      key={s?.category || idx}
                      className="relative flex flex-col sm:grid sm:grid-cols-[1fr_90px_110px_90px_80px] gap-2 sm:items-center px-3 py-3 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)] hover:border-[rgba(0,229,255,0.15)] transition-all duration-200"
                    >
                      {/* Category + tooltip */}
                      <div className="flex items-center gap-2">
                        <span className="text-[#E2E8F0] text-sm font-medium">{s?.category}</span>
                        <button
                          onMouseEnter={() => setTooltipIdx(idx)}
                          onMouseLeave={() => setTooltipIdx(null)}
                          onFocus={() => setTooltipIdx(idx)}
                          onBlur={() => setTooltipIdx(null)}
                          className="text-[#475569] hover:text-[#00E5FF] transition-colors flex-shrink-0"
                          aria-label="View reasoning"
                        >
                          <Icon name="Info" size={13} />
                        </button>
                        {tooltipIdx === idx && (
                          <div className="absolute left-3 top-full mt-1 z-20 w-64 p-3 rounded-xl bg-[#0D2D3A] border border-[rgba(0,229,255,0.2)] shadow-xl">
                            <p className="text-[#E2E8F0] text-xs leading-relaxed">{s?.reasoning}</p>
                          </div>
                        )}
                      </div>

                      {/* Mobile row */}
                      <div className="flex items-center justify-between sm:contents gap-2">
                        <div className="flex items-center gap-3 sm:contents">
                          <span className="text-[#94A3B8] text-xs sm:text-sm text-right font-mono">${(s?.current_budget || 0)?.toLocaleString()}</span>
                          <span className="text-[#00E5FF] text-xs sm:text-sm text-right font-mono font-semibold">${(s?.suggested_budget || 0)?.toLocaleString()}</span>
                          <div className="flex items-center gap-1">
                            <Icon name={changeIcon} size={12} color={changeColor} />
                            <span className="text-xs font-mono font-semibold" style={{ color: changeColor }}>
                              {isIncrease ? '+' : ''}{(s?.change_amount || 0)?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleApply(s, idx)}
                          disabled={applyingIdx === idx || applyingAll}
                          className="px-2.5 py-1.5 rounded-lg bg-[rgba(0,229,255,0.1)] border border-[rgba(0,229,255,0.2)] text-[#00E5FF] text-xs font-medium hover:bg-[rgba(0,229,255,0.2)] transition-colors disabled:opacity-50 flex items-center justify-center min-w-[52px]"
                        >
                          {applyingIdx === idx ? (
                            <div className="w-3 h-3 border border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
                          ) : 'Apply'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Apply All */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleApplyAll}
                  disabled={applyingAll || applyingIdx !== null}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#00E5FF] to-[#06B6D4] text-[#0D1B2A] font-semibold text-sm hover:opacity-90 transition-all duration-200 disabled:opacity-60 shadow-md shadow-[rgba(0,229,255,0.15)]"
                >
                  {applyingAll ? (
                    <><div className="w-4 h-4 border-2 border-[#0D1B2A] border-t-transparent rounded-full animate-spin" />Applying...</>
                  ) : (
                    <><Icon name="CheckCheck" size={15} color="#0D1B2A" />Apply All Suggestions</>
                  )}
                </button>
              </div>

              {/* Goal-Aligned Insights */}
              {goalInsights && (
                <div className="mt-5 p-4 rounded-xl bg-[rgba(0,229,255,0.05)] border border-[rgba(0,229,255,0.15)] backdrop-blur-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Target" size={15} color="#00E5FF" />
                    <span className="text-[#00E5FF] text-xs font-semibold tracking-wide uppercase">Goal-Aligned Insights</span>
                  </div>
                  <p className="text-[#CBD5E1] text-sm leading-relaxed">{goalInsights}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { AiBudgetOptimizer };

